import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { isOwnerRole } from '../shared/authority';
import GlobalQuickActions from '../components/GlobalQuickActions';
import { EmergencyBookingNotificationBadge } from '../components/staff/EmergencyBookingNotificationBadge';
import ManagerApprovalModal from '../components/ManagerApprovalModal';
import LockScreen from '../components/LockScreen';
import { discountApprovalService } from '../services/discountApprovalService';
import { websiteConfigService } from '../services/websiteConfigService';
import { useInactivityLock } from '../hooks/useInactivityLock';
import { sessionAuditService } from '../services/sessionAuditService';
import { usePinLock } from '../contexts/PinLockContext';
import { usePermission } from '../hooks/usePermission';
import {
  Home, Calendar, Scissors, Users, Package,
  BarChart, Settings, Menu,
  LogOut,
  CreditCard, MessageSquare,
  DollarSign, Star, Shield, Brain, Lock, Megaphone
} from 'lucide-react';
import { CashRegister } from '../components/icons/CashRegister';
import BarberStationIndicator from '../components/auth/BarberStationIndicator';
import AIAssistantChat from '../components/ai/AIAssistantChat';
import NotificationBell from '../components/header/NotificationBell';
import ConnectCenter from '../components/messaging/ConnectCenter';
import { communicationService, ConnectThread } from '../services/communicationService';
import { barberDataService } from '../services/barberDataService';
import { isTerminalAvailable, warmConnectReader } from '../services/terminalService';

interface MainLayoutProps {
  children?: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const { syncThemeWithBarber } = useTheme();
  const { signOutStation, currentBarber, lock: lockStation } = usePinLock();
  const { isManager, isOwner } = usePermission(); // shop-account controls are manager/owner only

  // Sync theme preference from Firebase when barber logs in
  useEffect(() => {
    if (currentBarber?.barberId) {
      syncThemeWithBarber(currentBarber.barberId);
    }
  }, [currentBarber?.barberId, syncThemeWithBarber]);
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  // Sidebar logo — read from the shared websiteConfig so uploading a logo in Settings updates the
  // staff sidebar too (same source as every customer surface). Falls back to the bundled mark.
  // WHITE engraved beard mark (transparent) — reads clean on the dark sidebar instead of a white box.
  // Overridden by a configured websiteConfig.logo if one is uploaded. (Jose 2026-10-17)
  const [shopLogo, setShopLogo] = useState('./assets/branding/beard-mark-white.png');
  useEffect(() => {
    websiteConfigService.getConfig().then(c => { if (c.logo) setShopLogo(c.logo); }).catch(() => {});
  }, []);
  // WARM-CONNECT the card reader once the app is up (Jose 2026-10-23): "look for the reader when the
  // app comes on and sync to it." So the reader is linked BEFORE the first checkout (no cold "Looking
  // for the reader…" wait), and resetting the app re-pairs without faking a sale. Native shells only;
  // fully best-effort + fire-and-forget — renders nothing and can never throw into the render path.
  useEffect(() => { if (isTerminalAvailable()) { warmConnectReader().catch(() => {}); } }, []);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isHovering, setIsHovering] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
  const [aiChatOpen, setAiChatOpen] = useState(false);
  // SLIK Connect (client messaging): the top-bar icon + live unread badge. (Jose 2026-09-25)
  const [showConnect, setShowConnect] = useState(false);
  const [connectUnread, setConnectUnread] = useState(0);
  const [connectInitialThread, setConnectInitialThread] = useState<ConnectThread | null>(null);
  // 💬 SLIK Connect LAYER 2 (Jose 2026-10-21): a PERSISTENT "New message" popup at the app shell so an
  // inbound client text is NEVER missed — it pops forward (green), survives refresh (reads live unread),
  // and stays until opened or dismissed (then the top-bar badge holds the count). Chime on a new arrival.
  const [newMsgThread, setNewMsgThread] = useState<ConnectThread | null>(null);
  const [newMsgCount, setNewMsgCount] = useState(0);
  const prevUnreadRef = useRef(0);
  const dismissedKeyRef = useRef('');
  const firstLoadRef = useRef(true);
  const chimeMutedRef = useRef<boolean>((() => { try { return localStorage.getItem('slik-connect-chime-muted') === '1'; } catch { return false; } })());
  const [chimeMuted, setChimeMuted] = useState(chimeMutedRef.current);
  const playChime = () => {
    if (chimeMutedRef.current) return;
    try {
      const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      const now = ctx.currentTime;
      [880, 1175].forEach((freq, i) => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.type = 'sine'; o.frequency.value = freq;
        o.connect(g); g.connect(ctx.destination);
        const t = now + i * 0.16;
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.18, t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
        o.start(t); o.stop(t + 0.32);
      });
      setTimeout(() => { try { ctx.close(); } catch { /* noop */ } }, 1200);
    } catch { /* audio best-effort */ }
  };
  useEffect(() => {
    const unsub = communicationService.subscribeToThreads((threads) => {
      const total = threads.reduce((sum, t) => sum + (t.unread || 0), 0);
      setConnectUnread(total);
      setNewMsgCount(total);
      if (total === 0) { setNewMsgThread(null); prevUnreadRef.current = 0; firstLoadRef.current = false; return; }
      const newest = threads
        .filter((t) => (t.unread || 0) > 0)
        .sort((a, b) => (b.lastAt?.getTime?.() || 0) - (a.lastAt?.getTime?.() || 0))[0] || null;
      const increased = total > prevUnreadRef.current;
      prevUnreadRef.current = total;
      if (!newest) return;
      const key = `${newest.phoneDigits}|${newest.lastAt?.getTime?.() || 0}`;
      if (increased) {
        // a NEW inbound → always re-raise (even if previously dismissed) + chime (not on first load)
        dismissedKeyRef.current = '';
        setNewMsgThread(newest);
        if (!firstLoadRef.current) playChime();
      } else if (key !== dismissedKeyRef.current) {
        // on load / unchanged with existing unread → show unless this exact message was dismissed
        setNewMsgThread(newest);
      }
      firstLoadRef.current = false;
    });
    return () => unsub();
  }, []);
  const openNewMsgThread = () => {
    if (!newMsgThread) return;
    setConnectInitialThread(newMsgThread);
    setShowConnect(true);
    communicationService.markThreadRead(newMsgThread.phoneDigits).catch(() => {});
    setNewMsgThread(null);
  };
  const dismissNewMsg = () => {
    if (newMsgThread) dismissedKeyRef.current = `${newMsgThread.phoneDigits}|${newMsgThread.lastAt?.getTime?.() || 0}`;
    setNewMsgThread(null);
  };
  const toggleChimeMute = () => {
    const next = !chimeMutedRef.current;
    chimeMutedRef.current = next; setChimeMuted(next);
    try { localStorage.setItem('slik-connect-chime-muted', next ? '1' : '0'); } catch { /* noop */ }
  };

  // Auto-lock configuration - DISABLED for now
  // const INACTIVITY_TIMEOUT = 60000; // 60 seconds (can be changed to 30000 for 30 seconds)

  // Inactivity lock hook - DISABLED during development
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { isLocked, lock, unlock } = useInactivityLock({
    timeout: 60000,
    enabled: false, // DISABLED - set to !!user to re-enable
  });

  // Detect if we're on the booking/operations view
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isBookingView = location.pathname === '/operations' || location.pathname === '/booking';

  // isManager comes from usePermission() above — it resolves the PIN OPERATOR's role (owner PIN'd
  // in over the shop account reads as manager), which is what the shop-account controls need.
  // (Removed the old auth-role-only isManager 2026-09-21 — it read the register's staff role and
  // hid the controls even for a real owner.)

  // Check if current user has AI access (owner or admin only)
  // Priority: Station barber role takes precedence over Firebase user role
  const firebaseRole = (user?.role as string)?.toLowerCase();
  const stationBarber = currentBarber?.barberId
    ? barberDataService.getBarberById(currentBarber.barberId)
    : null;
  const stationRole = stationBarber?.role?.toLowerCase();
  const effectiveRole = currentBarber?.barberId ? stationRole : firebaseRole;
  const hasAiAccess = isOwnerRole(effectiveRole);

  // Consolidated navigation. `barber:true` = a barber operator sees it (calendar/register/own growth);
  // everything else is manager/owner and hidden from a barber's PIN session (matches the route gate).
  const navigation = [
    // Core Business
    { name: 'Dashboard', href: '/dashboard', icon: Home },
    { name: 'Operations', href: '/operations', icon: Calendar, barber: true },
    { name: 'Register', href: '/pos', icon: CashRegister, barber: true },
    { name: 'Clients & Staff', href: '/people', icon: Users },
    { name: 'Coaching', href: '/coaching', icon: Brain },
    { name: 'My Growth', href: '/my-coaching', icon: Star, barber: true },
    { name: 'Services', href: '/services', icon: Scissors },
    { name: 'Inventory', href: '/inventory', icon: Package },

    // Intelligence & Growth
    { name: 'Analytics', href: '/analytics', icon: BarChart },
    { name: 'Marketing', href: '/marketing', icon: Megaphone },
    // Training and Partnership modules temporarily hidden for production
    // { name: 'Training', href: '/training', icon: BookOpen },
    // { name: 'Partnership', href: '/partnership', icon: Handshake },

    // Administration
    { name: 'Financial', href: '/financial', icon: DollarSign },
    { name: 'Communication', href: '/communication', icon: MessageSquare },
    { name: 'Settings', href: '/settings', icon: Settings },
  // Hide the manager/owner items when a barber is the operator (owner logged in as themselves resolves
  // to manager → full nav). Enforced everywhere as of the prod one-shot (2026-09-26).
  ].filter((item) => isManager || (item as any).barber);

  // Handle responsive behavior for different devices
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 768) {
        // Phone: hide sidebar, use bottom nav
        setSidebarOpen(false);
      } else {
        // iPad/Tablet/Desktop: show sidebar
        setSidebarOpen(true);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Poll for pending approval requests (managers only)
  useEffect(() => {
    if (!isManager) return;

    const loadPendingCount = async () => {
      try {
        const requests = await discountApprovalService.getPendingApprovals();
        setPendingApprovalCount(requests.length);
      } catch (error) {
        console.error('Error loading pending approvals:', error);
      }
    };

    // Load immediately
    loadPendingCount();

    // Poll every 30 seconds
    const interval = setInterval(loadPendingCount, 30000);
    return () => clearInterval(interval);
  }, [isManager]);

  // Log session lock event when screen locks
  useEffect(() => {
    if (isLocked && user) {
      sessionAuditService.logLock(
        {
          id: user.id,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role as string,
          employeeNumber: (user as any).employeeNumber,
        },
        true // isInactivityLock = true
      );
    }
  }, [isLocked, user]);

  // Handle unlock - log session unlock event
  const handleUnlock = async () => {
    if (user) {
      await sessionAuditService.logUnlock({
        id: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.role as string,
        employeeNumber: (user as any).employeeNumber,
      });
    }
    unlock(); // Call the unlock function from useInactivityLock hook
  };

  // If screen is locked, show lock screen
  if (isLocked && user) {
    return (
      <LockScreen
        onUnlock={handleUnlock}
        lastUser={{
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role as string,
          employeeNumber: (user as any).employeeNumber,
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Icon-Only Sidebar - Always Visible */}
      <div
        className="fixed inset-y-0 left-0 z-50 w-20 transition-all duration-300 shadow-2xl"
        style={{
          background: 'var(--nav-gradient)',   /* warm faded nav (was hardcoded purple #667eea→#764ba2) */
          transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
        }}
      >
        <div className="flex h-full flex-col">
          {/* Logo - Configurable Shop Logo */}
          <div className="flex h-16 items-center justify-center border-b border-purple-700/30 p-2">
            <img
              src={shopLogo}
              alt="Shop Logo"
              className="h-12 w-12 object-contain"
              onError={(e) => {
                // Fallback to text if image not found
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                target.parentElement!.innerHTML = '<span class="text-xl font-bold text-white">SL</span>';
              }}
            />
          </div>

          {/* Navigation Items - Icons Only with larger touch targets for iPad */}
          <nav className="flex-1 space-y-1 px-2 py-4 overflow-y-auto scrollbar-hide">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center justify-center p-3.5 rounded-lg transition-all duration-200 touch-manipulation ${
                    isActive
                      ? 'bg-white/20 text-white shadow-lg scale-105'
                      : 'text-white/80 hover:bg-white/10 hover:text-white active:bg-white/20'
                  }`}
                  title={item.name}
                >
                  <item.icon className="h-6 w-6 flex-shrink-0" />
                </Link>
              );
            })}
          </nav>

          {/* User section - operator (PIN) on top of the shop account (Firebase).
              pb clears the mobile bottom tab bar (Jose 2026-09-21: the logout was clipped off-screen). */}
          <div className="border-t border-purple-700/30 p-4 pb-24 lg:pb-4 flex-shrink-0">
            <div className="flex flex-col items-center space-y-2">
              {/* Show current barber initials (from station login) */}
              <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center text-white font-semibold text-sm">
                {currentBarber?.barberName?.split(' ').map(n => n[0]).join('') || user?.firstName?.[0] || '?'}
              </div>
              {/* Operator name — everyone. WHO-AM-I shop email + shop-account controls are
                  MANAGER/OWNER only (Jose 2026-09-21): a barber shouldn't see or switch the
                  underlying shop login; they get the lock + switch-barber only. */}
              {currentBarber?.barberName && (
                <div className="text-white text-xs font-semibold">{currentBarber.barberName}</div>
              )}
              {/* Shop-account email — MANAGER+ ONLY. A barber operator must not see the shop login. */}
              {isManager && user?.email && (
                <div className="text-white/50 text-[10px] break-all text-center" title="Shop account this station is signed in as">
                  {user.email}
                </div>
              )}
              <div className="flex items-center gap-4 pt-1">
                {/* Lock the screen — available to EVERYONE incl. barbers (step-away lock). */}
                {currentBarber?.hasPin && (
                  <button
                    onClick={() => lockStation()}
                    className="text-white/70 hover:text-white transition-colors"
                    title="Lock screen"
                  >
                    <Lock className="h-5 w-5" />
                  </button>
                )}
                {/* Switch barber — station/PIN only, keeps the shop account. Everyone. */}
                <button
                  onClick={() => { console.log('🔐 station sign-out'); signOutStation(); }}
                  className="text-white/70 hover:text-white transition-colors"
                  title="Switch barber (back to PIN pad — keeps the shop signed in)"
                >
                  <LogOut className="h-5 w-5" />
                </button>
                {/* Full shop-account logout — MANAGER+ ONLY. A barber operator must NOT be able to sign
                    the shop account out / switch to a different login (they could try to create an
                    account). Barbers use "Switch barber" (back to the PIN pad) instead. (Jose 2026-09-27) */}
                {isManager && (
                  <button
                    onClick={async () => {
                      // NO window.confirm — it silently no-ops in the native WKWebView, which trapped
                      // Jose (logout "did nothing"). Fire logout directly; logout() hard-navigates to
                      // /login so it's obvious. Accidental? Just log back in. (Jose 2026-09-23)
                      try { await logout(); } catch (e) { console.error('logout failed', e); }
                    }}
                    className="text-red-300/80 hover:text-red-200 transition-colors text-[10px] font-semibold uppercase tracking-wide"
                    title="Sign out of the shop account (switch to a different login)"
                  >
                    Switch Account
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && window.innerWidth < 768 && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className={`transition-all duration-300 h-screen flex flex-col min-w-0 overflow-x-hidden ${sidebarOpen ? 'ml-20' : 'ml-0'}`}>
        {/* Top bar — outer wrapper carries pt-safe so the header bg fills the iOS status-bar/notch
            area (native edge-to-edge WebView) and the h-16 row sits BELOW it. Needs viewport-fit=cover
            in index.html for env(safe-area-inset-top) to be nonzero. (Jose 2026-09-24) */}
        <div className="sticky top-0 z-[45] bg-white dark:bg-gray-800 shadow pt-safe">
        <div className="flex h-16 items-center justify-between px-4">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-gray-500 focus:outline-none hover:text-gray-700 transition-colors md:hidden"
            title="Toggle sidebar"
          >
            <Menu className="h-6 w-6" />
          </button>
          {/* Spacer for desktop when hamburger is hidden */}
          <div className="hidden md:block w-6"></div>

          <div className="flex items-center space-x-4">
            {/* Barber Station Indicator */}
            <BarberStationIndicator />

            {/* Shop-account full SIGN OUT — OWNER ONLY now that the operator model is enforced everywhere
                (prod one-shot 2026-09-26). A barber tapping in shouldn't sign the whole shop account out;
                they LOCK the screen (initials → Lock Screen) to hand off, and the operator PIN pad has its
                own "bypass → login" escape when there's no operator. */}
            {user?.email && isOwner && (
              <button
                onClick={async () => {
                  // NO window.confirm (native WKWebView no-ops it → logout looked dead). Fire directly.
                  try { await logout(); } catch (e) { console.error('logout failed', e); }
                }}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-red-50 hover:text-red-600 hover:border-red-300 transition-colors"
                title={'Sign out of shop account (' + (user?.email || '') + ')'}
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline text-xs font-semibold max-w-[140px] truncate">{user.email}</span>
              </button>
            )}

            {/* Digital Coins display removed (Jose 2026-09-27): it's a customer loyalty metric, always 0
                on a staff/shop account + not clickable. The NotificationBell (below) is the shop's
                notification surface — kept + wired. */}

            {/* Emergency Booking Alert Badge */}
            <EmergencyBookingNotificationBadge />

            {/* Manager Approval Badge (Managers Only) */}
            {isManager && pendingApprovalCount > 0 && (
              <button
                onClick={() => setShowApprovalModal(true)}
                className="relative text-orange-500 hover:text-orange-600 transition-colors"
                title="Pending Discount Approvals"
              >
                <Shield className="h-6 w-6" />
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold shadow-lg">
                  {pendingApprovalCount}
                </span>
              </button>
            )}

            {/* SLIK Connect — client messaging (opens the Connect center; badge = unread replies) */}
            <button
              onClick={() => setShowConnect(true)}
              className="relative text-gray-400 hover:text-purple-600 transition-colors"
              title="Messages"
            >
              <MessageSquare className="h-6 w-6" />
              {connectUnread > 0 && (
                <span className="absolute -top-1 -right-1 bg-purple-600 text-white text-[10px] rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center font-bold shadow">
                  {connectUnread > 99 ? '99+' : connectUnread}
                </span>
              )}
            </button>

            {/* Shop Notifications */}
            <NotificationBell />

            {/* AI Manager Button - Admin/Owner only */}
            {hasAiAccess && (
              <button
                onClick={() => setAiChatOpen(!aiChatOpen)}
                className={`relative text-gray-400 hover:text-purple-600 transition-colors ${
                  aiChatOpen ? 'text-purple-600' : ''
                }`}
                title="AI Manager"
              >
                <Brain className="h-6 w-6" />
                {/* Active indicator */}
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-green-400 rounded-full border-2 border-white dark:border-gray-800" />
              </button>
            )}

            {/* Theme toggle removed (Jose 2026-09-27): the app auto-themes; the manual toggle was dead. */}
          </div>
        </div>
        </div>

        {/* Page content - pb-20 adds padding for mobile bottom nav */}
        <main className="flex-1 min-h-0 pb-20 md:pb-0">
          {children || <Outlet />}
        </main>

        {/* Global Quick Actions - Available everywhere */}
        <GlobalQuickActions />

        {/* AI Assistant Chat - Admin only (owner/admin) */}
        {hasAiAccess && (
          <AIAssistantChat
            isOpen={aiChatOpen}
            onClose={() => setAiChatOpen(false)}
          />
        )}

        {/* Mobile Bottom Navigation - Only visible on phones */}
        <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 md:hidden pb-safe">
          <div className="flex items-center justify-around h-16">
            {[
              { name: 'Dashboard', href: '/dashboard', icon: Home },
              { name: 'Operations', href: '/operations', icon: Calendar, barber: true },
              { name: 'Register', href: '/pos', icon: CashRegister, barber: true },
              { name: 'Clients', href: '/people', icon: Users },
              { name: 'More', href: '#more', icon: Menu, barber: true },
            ].filter((item) => isManager || (item as any).barber).map((item) => {
              const isActive = location.pathname === item.href;
              const isMore = item.href === '#more';
              return (
                <button
                  key={item.name}
                  onClick={() => {
                    if (isMore) {
                      setSidebarOpen(!sidebarOpen);
                    } else {
                      navigate(item.href);
                    }
                  }}
                  className={`flex flex-col items-center justify-center flex-1 h-full touch-manipulation transition-all active:scale-95 ${
                    isActive
                      ? 'text-purple-600'
                      : 'text-gray-500 active:text-purple-500'
                  }`}
                >
                  <item.icon className={`w-6 h-6 ${isActive ? 'scale-110' : ''}`} />
                  <span className="text-[10px] mt-1 font-medium">{item.name}</span>
                </button>
              );
            })}
          </div>
        </nav>

        {/* Manager Approval Modal (Managers Only) */}
        {isManager && (
          <ManagerApprovalModal
            isOpen={showApprovalModal}
            onClose={() => {
              setShowApprovalModal(false);
              // Reload count after closing modal
              discountApprovalService.getPendingApprovals()
                .then((requests: any) => setPendingApprovalCount(requests.length))
                .catch((error: any) => console.error('Error reloading pending count:', error));
            }}
          />
        )}

        {/* SLIK Connect — client messaging center */}
        {showConnect && <ConnectCenter initialThread={connectInitialThread} operatorName={currentBarber?.barberName || user?.firstName} onClose={() => { setShowConnect(false); setConnectInitialThread(null); }} />}

        {/* 💬 SLIK Connect LAYER 2 — persistent "New message" popup (Jose 2026-10-21). Front-and-center,
            green, survives refresh, stays until Open or Dismiss. */}
        {newMsgThread && !showConnect && (
          <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[10080] w-[min(92vw,420px)] pointer-events-auto">
            <div className="slik-newmsg-pop rounded-2xl overflow-hidden shadow-2xl border-2 border-emerald-400/70" style={{ background: '#151b2b', boxShadow: '0 0 50px rgba(16,185,129,0.45)' }}>
              <div className="h-1.5 bg-gradient-to-r from-emerald-400 to-emerald-500 animate-pulse" />
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <div className="relative flex-shrink-0">
                    <span className="absolute inset-0 rounded-full bg-emerald-400/30 animate-ping" />
                    <div className="relative w-11 h-11 rounded-full bg-emerald-500 flex items-center justify-center shadow">
                      <MessageSquare className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-bold uppercase tracking-wide text-emerald-300">New message</span>
                      {newMsgCount > 1 && <span className="text-[10px] font-bold text-white/70 bg-white/10 rounded-full px-1.5 py-0.5">+{newMsgCount - 1} more</span>}
                    </div>
                    <div className="text-base font-bold text-white truncate">{newMsgThread.clientName}</div>
                    <div className="text-sm text-white/70 truncate">{newMsgThread.lastMessage}</div>
                  </div>
                  <div className="flex flex-col items-center gap-1 flex-shrink-0">
                    <button onClick={toggleChimeMute} title={chimeMuted ? 'Unmute chime' : 'Mute chime'} className="text-white/40 hover:text-white/80 text-sm">{chimeMuted ? '🔇' : '🔔'}</button>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={openNewMsgThread} className="flex-1 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm active:scale-95 transition">Open message</button>
                  <button onClick={dismissNewMsg} className="px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white/80 font-semibold text-sm active:scale-95 transition">Dismiss</button>
                </div>
              </div>
            </div>
            <style>{`
              @keyframes slikNewMsgPop { 0% { transform: translateY(-16px) scale(0.96); opacity: 0; } 60% { transform: translateY(0) scale(1.02); opacity: 1; } 100% { transform: translateY(0) scale(1); opacity: 1; } }
              .slik-newmsg-pop { animation: slikNewMsgPop 0.4s cubic-bezier(0.22,1,0.36,1) both; }
            `}</style>
          </div>
        )}
      </div>
    </div>
  );
};

export default MainLayout;
